import CardTravelIcon from '@material-ui/icons/CardTravel';
import LinearProgress from '@material-ui/core/LinearProgress';
import withStyles from '@material-ui/styles/withStyles';
import axios from 'axios';
import * as _ from 'lodash';
import * as moment from 'moment';
import * as PropTypes from 'prop-types';
import React, { Component } from 'react';
import { hot } from 'react-hot-loader/root';
import itineraryLoadingGif from '../../assets/gifs/itinerary-loading.gif';
import logo from '../../assets/svg/logo.svg';
import ChatPanel from '../../components/aiItineraryBuilder/chat-panel';
import ClientItineraryGallery from '../../components/aiItineraryBuilder/clientItineraryGallery';
import FlynoteStats from '../../components/aiItineraryBuilder/flynoteStats';
import GalleryModal from '../../components/aiItineraryBuilder/galleryModal';
import ItinerarySection from '../../components/aiItineraryBuilder/itinerarySection';
import PricingPanel from '../../components/aiItineraryBuilder/pricingPanel';
import { baseURL } from '../../utils/apiEndpoints';
import { TIMEOUT } from '../../utils/consts';

const mainLayoutStyle = {
  display: 'flex',
  width: '100%',
  height: 'calc(100vh - 56px)', // Subtract header height
  background: '#F5F5F5',
  fontFamily: 'Sofia-pro, sans-serif',
  overflow: 'hidden', // Prevent main container from scrolling
};

const rightPanelStyle = {
  flex: 1,
  background: '#F5F5F5',
  display: 'flex',
  flexDirection: 'column',
  height: '100%',
  overflow: 'hidden', // Prevent right panel from scrolling
};

const tabBarStyle = {
  display: 'flex',
  height: 32,
  alignItems: 'center',
  fontFamily: 'Sofia-pro, sans-serif',
  background: '#F5F5F5',
  flexShrink: 0, // Prevent tabs from shrinking
  zIndex: 10, // Ensure tabs stay on top
};

const tabContentStyle = {
  flex: 1,
  background: '#fff',
  padding: 0,
  overflow: 'auto', // Only this area should scroll
  display: 'flex',
  flexDirection: 'column',
};

const tabData = [
  {
    key: 'client',
    label: 'Client Itinerary',
    icon: <CardTravelIcon style={{ fontSize: 16, marginRight: 8 }} />,
  },
];

class AiItineraryBuilder extends Component {
  ITINERARY_LOADING_TIME_MINUTES = 5;

  loadingMessages = [
    '✈️ Plotting the plan… ~xxx mins left. Maybe plan your next coffee stop ☕',
    '🧳 Packing the details… ~xxx mins left. Perfect time to unpack that snack.',
    '🗺️ Mapping perfection… ~xxx mins left — stretch like you\'re boarding a long-haul.',
    '🏝️ Shaping paradise… Almost done - ~xxx min more. Go book that spa you\'ve been avoiding.',
    '🌅 Curating sunsets… ~xxx mins more— tidy that desk before your next call.',
    '🍹 Shaking up the itinerary… ~xxx mins till ready — grab a drink.',
    '🚗 Fueling the adventure... ~xxx mins to roll. Top up your energy too!',
    '🏝️ Crafting wanderlust... Just xxx mins more. Time to refill your chai ☕',
    '📸 Lining up the views... ~xxx mins. Scroll Insta, call it "research."',
    '🎒 Trip loading... ~xxx mins. You could chase one more lead — or a snack.',
  ];

  constructor(props) {
    super(props);

    this.state = {
      activeTab: 'client',
      isChatMinimized: false,
      chatLoading: false,
      galleryState: {
        isOpen: false,
        images: [],
        initialIndex: 0,
      },
      // storageManagerOpen: false,
      activeModal: null, // null | 'cost' | 'sell' | 'cruisePortal' | 'hotelPortal'
      selectedComponentId: null, // Store selected component for portal modals
      itineraryData: {},
      firstMessageAt: null,
      selectedLoadingMessage: null,
      currentTime: Date.now(), // For updating progress bar every 5 seconds
    };
    
    this.progressInterval = null;
  }

  componentDidMount() {
    // Set up interval to update progress bar every second for granular updates
    this.progressInterval = setInterval(() => {
      const { firstMessageAt, itineraryData } = this.state;
      if (firstMessageAt && _.isEmpty(itineraryData)) {
        this.setState({ currentTime: Date.now() });
      }
    }, 1000);
  }

  componentDidUpdate(prevProps, prevState) {
    // Clear interval when itineraryData becomes non-empty
    const { itineraryData } = this.state;
    if (_.isEmpty(prevState.itineraryData) && !_.isEmpty(itineraryData)) {
      if (this.progressInterval) {
        clearInterval(this.progressInterval);
        this.progressInterval = null;
      }
    }
  }

  componentWillUnmount() {
    // Clean up interval when component unmounts
    if (this.progressInterval) {
      clearInterval(this.progressInterval);
    }
  }

  openGallery = (images, initialIndex = 0) => {
    this.setState({
      galleryState: {
        isOpen: true,
        images,
        initialIndex,
      },
    });
  };

  closeGallery = () => {
    this.setState({
      galleryState: {
        isOpen: false,
        images: [],
        initialIndex: 0,
      },
    });
  };

  openPricingModal = (modalType, selectedComponentId = null) => {
    this.setState({ activeModal: modalType, selectedComponentId });
  };

  closePricingModal = () => {
    this.setState({ activeModal: null, selectedComponentId: null });
  };

  handlePrimaryButtonClick = (modalType) => {
    this.openPricingModal(modalType);
  };

  handleChatMinimizeChange = (minimized) => {
    this.setState({ isChatMinimized: minimized });
  };

  setActiveTab = (tab) => {
    this.setState({ activeTab: tab });
  };

  handleChatLoadingChange = (loading) => {
    this.setState({ chatLoading: loading });
  };

  updateItineraryData = async (updatedData) => {
    this.setState({ itineraryData: updatedData || {} });
  }

  setFirstMessageAt = (timestamp) => {
    this.setState({
      firstMessageAt: timestamp,
      selectedLoadingMessage: _.sample(this.loadingMessages),
    });
  }

  updateCostPrice = async (itinerary) => {
    const { match, showSnackbar } = this.props;
    const itineraryId = match?.params?.itineraryId;

    try {
      const response = await axios.put(`${baseURL}itineraries/${itineraryId}/updateCostPrice`, {
        itinerary,
      }, {
        headers: {
          'Content-Type': 'application/json',
        },
      });
      this.setState({ itineraryData: response.data.itinerary });
      return { success: true };
    } catch (error) {
      showSnackbar('Unable to update cost price', 'error');
      return { success: false };
    }
  }

  handleGetPDF = async () => {
    const { match, showSnackbar } = this.props;
    const itineraryId = match?.params?.itineraryId;

    showSnackbar('PDF will be downloaded shortly', 'success');
    try {
      const response = await axios({
        method: 'put',
        url: `${baseURL}itineraries/${itineraryId}/updateItineraryPdf`,
        timeout: TIMEOUT,
      });
      if (_.isNil(response.data?.pdfUrl)) {
        throw new Error('PDF download failed');
      } else {
        const link = document.createElement('a');
        link.href = response.data.pdfUrl;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        showSnackbar('PDF download completed', 'success');
      }
    } catch (error) {
      showSnackbar('PDF download failed, please retry.', 'error');
    }
  }

  handleGetLink = () => {
    const { itineraryData } = this.state;
    const { showSnackbar } = this.props;
    if (navigator.clipboard && itineraryData.url) {
      navigator.clipboard.writeText(itineraryData.url);
      showSnackbar('Link copied to clipboard', 'success');
    }
  }

  render() {
    function formatDate(dateStr) {
      if (!dateStr) return '';
      const d = new Date(dateStr);
      return d.toLocaleDateString('en-GB');
    }

    function getDuration(from, to) {
      if (!from || !to) return '';
      const start = new Date(from);
      const end = new Date(to);
      const diff = Math.max(0, (end - start) / (1000 * 60 * 60 * 24));
      return `${diff + 1} days, ${diff} Nights`;
    }

    function formatLoadingMessage(message) {
      if (!message) return '';
      // Match patterns like "~xxx mins", "~xxx min", "xxx mins", "xxx min"
      // This regex matches: optional ~, digits, optional space, "min" or "mins"
      const timePattern = /(~?\d+\s*min(?:s)?)/gi;
      const parts = [];
      let lastIndex = 0;
      let match = timePattern.exec(message);

      while (match !== null) {
        // Add text before the match
        if (match.index > lastIndex) {
          parts.push(message.substring(lastIndex, match.index));
        }
        // Add the matched time portion wrapped in bold
        parts.push(<strong key={match.index}>{match[0]}</strong>);
        lastIndex = match.index + match[0].length;
        match = timePattern.exec(message);
      }
      // Add remaining text after the last match
      if (lastIndex < message.length) {
        parts.push(message.substring(lastIndex));
      }
      // If no matches found, return the original message
      return parts.length > 0 ? parts : message;
    }

    const {
      classes,
      expert,
      showSnackbar,
      match,
    } = this.props;
    const {
      activeTab,
      isChatMinimized,
      chatLoading,
      galleryState,
      // storageManagerOpen,
      activeModal,
      selectedComponentId,
      itineraryData = {},   
      firstMessageAt,
      selectedLoadingMessage,
      currentTime,
    } = this.state;
    
    const TripOverview = () => {
      const { requirement = {} } = itineraryData;
      const destination = (requirement.places || []).map((p) => p.name || p.value || '').filter(Boolean).join(', ') || 'N.A.';      
      const from = requirement.departureDate;
      const to = requirement.arrivalDate;
      const dates = from && to ? `${formatDate(from)} - ${formatDate(to)} (${getDuration(from, to)})` : 'N.A.';
      const travellers = requirement.travellers
        ? [
          requirement.travellers.adults ? `${requirement.travellers.adults} Adult${requirement.travellers.adults > 1 ? 's' : ''}` : '',
          requirement.travellers.children ? `${requirement.travellers.children} Child${requirement.travellers.children > 1 ? 'ren' : ''}` : '',
          requirement.travellers.infants ? `${requirement.travellers.infants} Infant${requirement.travellers.infants > 1 ? 's' : ''}` : '',
        ].filter(Boolean).join(', ')
        : 'N.A.';
      // Get occasion from localStorage, with better fallback logic
      let occassions;
      if (requirement.occassions && requirement.occassions.trim()) {
        occassions = requirement.occassions.replace('_', ' ');
      }
      const origin = (requirement.origin || []).map((o) => o.name || o.value || '').filter(Boolean).join(', ') || 'N.A.';

      return (
        <section
          style={{
            width: '100%',
            fontFamily: 'Sofia-pro, sans-serif',
            color: '#32302C',
          }}
        >
          <h2 style={{
            fontSize: 30, fontWeight: 700, margin: 0, marginBottom: 32,
          }}
          >
            Trip Overview
          </h2>
          <div style={{ fontSize: 16, lineHeight: '32px' }}>
            <div><span style={{ fontWeight: 700 }}>Destination(s):</span> <span>{destination}</span></div>          
            <div><span style={{ fontWeight: 700 }}>Dates:</span> <span>{dates}</span></div>
            <div><span style={{ fontWeight: 700 }}>Travellers:</span> <span>{travellers}</span></div>
            {occassions && <div><span style={{ fontWeight: 700 }}>Occasion:</span> <span>{occassions}</span></div>}
            <div><span style={{ fontWeight: 700 }}>Origin City:</span> <span>{origin}</span></div>
          </div>
        </section>
      );
    };
    
    // Extract itineraryId from URL params    
    const itineraryId = match?.params?.itineraryId;
    const requestUsId = match?.params?.requestUsId;
    const itineraryIdentifier = itineraryData.identifier;
    
    // Calculate changes saved text
    let changesSavedText;
    if (itineraryData.sentAt) {
      if (_.isNil(itineraryData.modifiedAt)) {
        changesSavedText = 'Changes saved';
      } else if (moment(itineraryData.modifiedAt).isAfter(moment(itineraryData.sentAt))) { 
        changesSavedText = `Saved at ${moment(itineraryData.modifiedAt).format('Do MMM h:mma').replace(':00', '')}`;
      } else {
        changesSavedText = `Sent at ${moment(itineraryData.sentAt).format('Do MMM h:mma').replace(':00', '')}`;
      }      
    } else {
      const now = moment();
      const modified = moment(itineraryData.modifiedAt);
      const days = now.diff(modified, 'days');
      if (days >= 1) {
        changesSavedText = `Changes saved ${days}d ago`;
      } else {
        const hours = now.diff(modified, 'hours');
        if (hours >= 1) {
          changesSavedText = `Changes saved ${hours}h ago`;
        } else {
          const minutes = now.diff(modified, 'minutes');
          changesSavedText = `Changes saved ${minutes}m ago`;
        }
      }
    }
    
    let primaryButtonText;
    let modalToOpen;
    // Calculate primary button text
    if (itineraryData.costPrice > 0) {
      if (itineraryData.sellPrice > 0) {
        if (_.isNil(itineraryData.itinerarySyncedAt)) {
          primaryButtonText = 'Send to Customer';
          modalToOpen = 'sell';
        } else {
          primaryButtonText = 'Get PDF/Links';
          modalToOpen = 'itinerarySent';
        }
      } else {
        primaryButtonText = 'Set Pricing';
        modalToOpen = 'sell';
      }      
    } else {
      primaryButtonText = 'Update Prices';
      modalToOpen = 'cost';
    }
    
    const AIItineraryHeader = (
      <div className={classes.headerStyle}>
        <div className={classes.leftStyle}>
          <img src={logo} alt="logo" className={classes.logoStyle} />         
        </div>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
          <div className={classes.tripTitleWrap}>
            <div className={classes.tripTitleRow}>
              <b>Client:</b>&nbsp; {itineraryData?.customerName } ({requestUsId}) &nbsp;&nbsp;<b>Itin. ID:</b> &nbsp;{itineraryIdentifier}
            </div>
            <span className={classes.changesSaved}>
              {changesSavedText}
            </span>
          </div>
          <button
            type="button"
            className={classes.primaryButton}
            style={{ marginRight: 0 }}
            onClick={() => this.handlePrimaryButtonClick(modalToOpen)}
            disabled={chatLoading}
          >
            {primaryButtonText}
          </button>
        </div>
      </div>
    );

    // Calculate remaining time in seconds for granular progress
    const totalLoadingSeconds = this.ITINERARY_LOADING_TIME_MINUTES * 60;
    const remainingSeconds = firstMessageAt 
      ? moment(firstMessageAt).add(this.ITINERARY_LOADING_TIME_MINUTES, 'minutes').diff(moment(currentTime), 'seconds')
      : 0;
    return (
      <div
        style={{
          width: '100vw',
          height: '100vh',
          overflow: 'hidden', // Prevent body scrolling
          fontFamily: 'Sofia-pro, sans-serif',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {/* Header - Fixed at top */}
        <div style={{ flexShrink: 0 }}>
          {AIItineraryHeader}
        </div>
        
        {/* Main Layout - Takes remaining height */}
        <div style={mainLayoutStyle}>
          {/* Left Panel (Chat) - Fixed width, no scroll */}
          <div style={{ flexShrink: 0 }}>
            <ChatPanel
              onMinimizeChange={this.handleChatMinimizeChange}
              onViewGallery={this.openGallery}
              updateItineraryData={this.updateItineraryData}
              onLoadingChange={this.handleChatLoadingChange}
              setFirstMessageAt={this.setFirstMessageAt}
              expert={expert}
            />
          </div>
          
          {/* Right Panel (Tabs) */}
          <div style={rightPanelStyle}>            
            {/* Normal layout when chat is expanded */}
            <>
              <div style={tabBarStyle}>
                {tabData.map((tab) => (
                  <div
                    key={tab.key}
                    role="tab"
                    tabIndex={0}
                    onClick={() => this.setActiveTab(tab.key)}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      fontSize: 14,
                      fontWeight: 600,
                      padding: '0 32px',
                      height: 32,
                      cursor: 'pointer',
                      background: activeTab === tab.key ? 'rgba(0,144,181,0.25)' : '#DDDDDD',
                      color: activeTab === tab.key ? '#0090B5' : '#666666',
                      // borderBottom: activeTab === tab.key ? '4px solid #0090B5' : 'none',
                      transition: 'all 0.2s',
                    }}
                  >
                    {tab.icon}
                    {tab.label}
                  </div>
                ))}
              </div>
                
              {/* Tab Content - Scrollable area */}
              <div style={tabContentStyle}>
                {_.isEmpty(itineraryData) && (
                  <div className={classes.loadingContainer}>
                    <div className={classes.loadingMessage}>
                      {
                        remainingSeconds > 0
                          ? formatLoadingMessage(selectedLoadingMessage?.replace('xxx', Math.ceil(remainingSeconds / 60)))
                          : 'Looks like this one\'s taking its sweet time…should be ready in a bit'
                      }
                    </div>
                    {
                      remainingSeconds > 0 ? (
                        <>
                          <div className={classes.progressContainer}>
                            <LinearProgress
                              variant="determinate"
                              value={(() => {
                                if (!firstMessageAt) {
                                  console.log('firstMessageAt is null/undefined');
                                  return 50; // Test value
                                }
                                const elapsedSeconds = totalLoadingSeconds - remainingSeconds;
                                const progress = Math.max(0, Math.min(100, (elapsedSeconds / totalLoadingSeconds) * 100));                                
                                return progress;
                              })()}
                              classes={{
                                root: classes.progressBar,
                                bar: classes.progress,
                              }}
                            />
                          </div>
                          <div>
                            You can move away from this tab, we&apos;ll notify you when ready
                          </div>
                        </>
                      ) : (
                        // <div>
                        //   <button
                        //     type="button"
                        //     className={classes.refreshButton}
                        //     onClick={() => window.location.reload()}
                        //     style={{ marginTop: 10 }}
                        //   >
                        //     Refresh Page
                        //   </button>
                        // </div>
                        null
                      )
                    }
                    <img
                      src={itineraryLoadingGif}
                      alt="Loading itinerary..."
                      style={{ width: 256, height: 256, marginTop: 50 }}
                    />
                  </div>
                )}
                                    
                {!_.isEmpty(itineraryData) && activeTab === 'client' && (
                  <>
                    {itineraryData.outOfSync && (
                      <div className={classes.outOfSyncBanner}>
                        Previous version is live & sent to customer{' '}
                        <button
                          type="button"
                          onClick={this.handleGetPDF}
                          className={classes.outOfSyncButton}
                        >
                          Get PDF
                        </button>
                        <button
                          type="button"
                          onClick={this.handleGetLink}
                          className={classes.outOfSyncButton}
                        >
                          Copy LINK
                        </button>
                      </div>
                    )}
                    <ClientItineraryGallery
                      openGallery={this.openGallery}
                      itineraryData={itineraryData}
                      isChatMinimized={isChatMinimized}
                    />
                    <div
                      style={{ position: 'relative', maxWidth: 840, margin: '0 auto' }}
                    >
                      <FlynoteStats />
                      <TripOverview />
                      <PricingPanel
                        onSellingPriceChange={this.setSellingPrice}
                        activeModal={activeModal}
                        selectedComponentId={selectedComponentId}
                        openPricingModal={this.openPricingModal}
                        closePricingModal={this.closePricingModal}
                        handlePrimaryButtonClick={() => this.handlePrimaryButtonClick(modalToOpen)}
                        expert={expert}
                        aiItineraryData={itineraryData}
                        updateItineraryData={this.updateItineraryData}
                        showSnackbar={showSnackbar}
                        requestUsId={requestUsId}
                        itineraryId={itineraryId}
                        updateCostPrice={this.updateCostPrice}
                        primaryButtonText={primaryButtonText}
                        chatLoading={chatLoading}
                        handleGetPDF={this.handleGetPDF}
                        handleGetLink={this.handleGetLink}
                      />
                      <ItinerarySection
                        onViewGallery={this.openGallery}
                        itineraryData={itineraryData}                        
                      />
                    </div>
                  </>
                )}
              </div>
            </>
          </div>
        </div>

        {/* Gallery Modal */}
        <GalleryModal
          isOpen={galleryState.isOpen}
          images={galleryState.images}
          initialIndex={galleryState.initialIndex}
          onClose={this.closeGallery}
        />

        {/* Storage Manager Modal */}
        {/* <StorageManager
          open={storageManagerOpen}
          onClose={() => this.setState({ storageManagerOpen: false })}
        /> */}
      </div>
    );
  }
}

/**
 * @param {import('@material-ui/core/styles/createMuiTheme').Theme} theme
 */
const styles = (theme) => ({
  headerStyle: {
    width: '100%',
    height: 56,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    background: 'linear-gradient(90deg, #0090B5 0%, #182326 100%)',
    color: '#fff',
    fontFamily: 'Sofia-pro, sans-serif',
    boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
    padding: '0 24px',
    position: 'relative',
    zIndex: 10,
  },
  leftStyle: {
    display: 'flex',
    alignItems: 'center',
    gap: 16,
  },
  logoStyle: {
    height: 24,
    marginRight: 24,
  },
  navButton: {
    background: 'transparent',
    color: '#fff',
    border: 'none',
    borderRadius: 0,
    padding: '8px 16px',
    fontFamily: 'Sofia-pro, sans-serif',
    fontWeight: 600,
    fontSize: 14,
    marginRight: 8,
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    gap: 8,
  },
  tripTitleWrap: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-end',
    marginRight: 24,
  },
  tripTitleRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    fontSize: 14,    
  },
  changesSaved: {
    fontSize: 12,
    opacity: 0.45,
    color: '#fff',
    fontFamily: 'Sofia-pro, sans-serif',
    marginTop: 2,
  },
  primaryButton: {
    background: theme.colors.white,
    color: theme.colors.primary,
    border: 'none',
    borderRadius: 17,
    height: 34,
    padding: '0 28px',
    fontFamily: 'Sofia-pro, sans-serif',
    fontWeight: 600,
    fontSize: 14,
    marginRight: 16,
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
  },
  refreshButton: {
    backgroundColor: '#0090B5',
    color: '#FFFFFF',
    border: 'none',
    borderRadius: 24,
    height: 36,
    padding: '0 40px',
    fontSize: 16,
    fontWeight: 600,
    cursor: 'pointer',
    fontFamily: 'Sofia-pro, sans-serif',
    '&:hover': {
      backgroundColor: '#007A94',
    },
  },
  loadingContainer: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    height: '100%',
    width: '100%',
    gap: '5px',
  },
  loadingMessage: {
    display: 'block',
    width: 700,
    fontSize: 30,
    color: theme.colors.textLight,
    textAlign: 'center',
  },
  progressContainer: {
    position: 'relative',
    width: 200,
    padding: '10px', 
  },
  progressBar: {
    height: '16px',
    borderRadius: '8px',
    maxWidth: '400px',
    margin: '0 auto',
    border: `1px solid ${theme.colors.primary}`,
    backgroundColor: theme.colors.white,
    boxSizing: 'border-box',
  },
  progress: {
    height: '14px',
    borderRadius: '8px',
    backgroundColor: theme.colors.primary,
  },
  outOfSyncBanner: {
    width: '100%',
    backgroundColor: theme.colors.primary,
    color: '#FFFFFF',
    textAlign: 'center',
    padding: '8px 24px',
    fontFamily: 'Sofia-pro, sans-serif',
    fontSize: 14,
  },
  outOfSyncButton: {
    background: 'transparent',
    border: 'none',
    color: '#FFFFFF',
    fontWeight: 700,
    cursor: 'pointer',
    fontFamily: 'Sofia-pro, sans-serif',
    fontSize: 14,
    padding: 0,
    margin: '0 8px',
    textDecoration: 'underline',
  },
});

AiItineraryBuilder.propTypes = {
  // AI Itinerary state
  itineraryData: PropTypes.object,

  // Auth state
  loginResp: PropTypes.object,
  isLogging: PropTypes.bool,
  loginError: PropTypes.bool,
  loginErrorMsg: PropTypes.string,

  // Common state
  suggestions: PropTypes.array,
  isFetchingSuggestions: PropTypes.bool,
  meta: PropTypes.object,
  conversion: PropTypes.object,
  conversionError: PropTypes.bool,
  conversionErrorMsg: PropTypes.string,

  // Action dispatchers
  clearAiItineraryData: PropTypes.func,
  getAutocompleteSuggestions: PropTypes.func,
  getConversion: PropTypes.func,
  updateStatus: PropTypes.func.isRequired,

  match: PropTypes.object.isRequired,
  classes: PropTypes.object.isRequired,
  expert: PropTypes.object.isRequired,
  showSnackbar: PropTypes.func,
};

export default hot(withStyles(styles)(AiItineraryBuilder)); 
